/**
 * Regenerate and instruct (docs/plans/lms-course-builder-plan.md 8.4, 6.11,
 * R6, 18.5; WP-B31): two buttons in a draft section's form, rendered through
 * ContentEditor's section-actions slot only when the builder is on (G20).
 *
 * - "Regenerate this section": a ConfirmModal with 8.4's warning; posts
 *   POST /admin/builder/versions/:id/regenerate { section_id }.
 * - "Apply an instruction": a Modal with a 1000-character textarea and
 *   examples; posts { section_id, instruction }.
 * - Both first save the editor's pending edits (beforeRun: the section run
 *   reads the saved draft, so an unsaved edit elsewhere would be lost), then
 *   hand the new run to the editor, which turns read-only and polls it.
 * - The remaining count ("6 of 10 builder passes left for this draft") sits
 *   under the buttons; at zero both are disabled and say why.
 * - A draft with no builder sources to rebuild from (written by hand, or its
 *   files withdrawn) answers LMS_BUILDER_NO_SOURCES unless a file is uploaded
 *   with the request (18.5): the dialogs then offer one file input and say so.
 *   The regenerate body carries no attestation (18.5): the policy notice is
 *   repeated beside the file input instead.
 */
import { useState } from 'react';
import { MessageSquarePlus, RefreshCw } from 'lucide-react';
import ConfirmModal from '../../../../../ui/ConfirmModal';
import Modal from '../../../../../ui/Modal';
import { fieldCls, outlineBtnCls, primaryBtnCls, secondaryBtnCls } from '../../../../../ui/styles';
import { BUILDER_FILE_ACCEPT, BUILDER_INSTRUCTION_MAX, regenerateSection, readBuilderError } from '../../../../api/builder';
import type { BuilderRun } from '../../../../api/builder';
import { noticeCls } from '../../../../components/player/styles';
import { builderFileProblem, explainError, UPLOAD_POLICY_TEXT } from './runModel';
import { hintCls, labelCls } from '../styles';

export interface SectionActionsProps {
  versionId: string;
  sectionId: string;
  /** 1-based, for the copy. */
  sectionNumber: number;
  /** Section runs already made on this draft, or null when unknown. */
  passesUsed: number | null;
  /** LMS_BUILDER_MAX_SECTION_RUNS_PER_VERSION (the config route's limits). */
  passesMax: number;
  /** True when the draft has no builder run to rebuild from: a file must go with the request. */
  needsFile: boolean;
  /** Disabled while a run is active or the editor cannot save. */
  disabled?: boolean;
  /** Save the editor's pending edits; false stops the request. */
  beforeRun: () => Promise<boolean>;
  /** The run was created (mode section): the editor locks and polls it. */
  onStarted: (run: BuilderRun) => void;
}

/** "6 of 10 builder passes left for this draft". */
export function passesLine(used: number | null, max: number): string {
  const left = Math.max(0, max - (used ?? 0));
  return `${left} of ${max} builder pass${max === 1 ? '' : 'es'} left for this draft`;
}

export const NO_SOURCES_TEXT =
  'This draft has no builder sources to rebuild from: it was written by hand, or the files it was built from were withdrawn. Upload the file this section should be written from.';

const EXAMPLES = ['Add a step about labelling', 'Make the reading shorter', 'Add two questions on the temperature range'];

type Dialog = 'regenerate' | 'instruct' | null;

export function SectionActions({
  versionId,
  sectionId,
  sectionNumber,
  passesUsed,
  passesMax,
  needsFile,
  disabled = false,
  beforeRun,
  onStarted,
}: SectionActionsProps) {
  const [dialog, setDialog] = useState<Dialog>(null);
  const [instruction, setInstruction] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [askFile, setAskFile] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const exhausted = passesUsed !== null && passesUsed >= passesMax;
  const wantFile = needsFile || askFile;
  const blocked = disabled || exhausted;

  const close = () => {
    if (busy) return;
    setDialog(null);
    setError(null);
    setFileError(null);
    setFile(null);
  };

  const open = (which: Dialog) => {
    setError(null);
    setFileError(null);
    setFile(null);
    if (which === 'instruct') setInstruction('');
    setDialog(which);
  };

  const pickFile = (list: FileList | null) => {
    const next = list && list.length > 0 ? list[0] : null;
    if (!next) {
      setFile(null);
      setFileError(null);
      return;
    }
    const problem = builderFileProblem(next);
    setFileError(problem);
    setFile(problem ? null : next);
  };

  const submit = async () => {
    if (busy) return;
    const text = instruction.trim();
    if (dialog === 'instruct' && !text) {
      setError('Write the instruction first.');
      return;
    }
    if (wantFile && !file) {
      setError('Choose the file this section should be written from.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const saved = await beforeRun();
      if (!saved) {
        setError('Your latest edits could not be saved, so the builder did not start. Save the draft, then try again.');
        return;
      }
      const run = await regenerateSection(versionId, {
        section_id: sectionId,
        instruction: dialog === 'instruct' ? text : null,
        file: wantFile ? file : null,
      });
      setDialog(null);
      setFile(null);
      setInstruction('');
      onStarted(run);
    } catch (err) {
      const problem = readBuilderError(err, 'The builder could not start on this section.');
      if (problem.code === 'LMS_BUILDER_NO_SOURCES') {
        setAskFile(true);
        setError(NO_SOURCES_TEXT);
      } else {
        setError(explainError(problem.code, problem.message));
      }
    } finally {
      setBusy(false);
    }
  };

  const fileField = wantFile ? (
    <div className="space-y-1.5" data-testid="lms-section-file">
      <label className={labelCls} htmlFor={`lms-section-file-${sectionId}`}>
        Source file (PDF, Word, text or Markdown)
      </label>
      <input
        id={`lms-section-file-${sectionId}`}
        type="file"
        accept={BUILDER_FILE_ACCEPT}
        className={`${fieldCls} w-full`}
        onChange={(e) => pickFile(e.target.files)}
        data-testid="lms-section-file-input"
      />
      {fileError ? <p className="text-xs text-red-700 dark:text-red-400">{fileError}</p> : null}
      <p className={hintCls}>{UPLOAD_POLICY_TEXT}</p>
    </div>
  ) : null;

  const errorBox = error ? (
    <div className={noticeCls('red')} role="alert" data-testid="lms-section-action-error">
      {error}
    </div>
  ) : null;

  return (
    <div className="space-y-1.5" data-testid="lms-section-actions">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          className={`${outlineBtnCls()} disabled:cursor-not-allowed disabled:opacity-50`}
          onClick={() => open('regenerate')}
          disabled={blocked}
          data-testid="lms-section-regenerate"
        >
          <RefreshCw className="h-4 w-4" aria-hidden="true" />
          Regenerate this section
        </button>
        <button
          type="button"
          className={`${outlineBtnCls()} disabled:cursor-not-allowed disabled:opacity-50`}
          onClick={() => open('instruct')}
          disabled={blocked}
          data-testid="lms-section-instruct"
        >
          <MessageSquarePlus className="h-4 w-4" aria-hidden="true" />
          Apply an instruction
        </button>
      </div>
      <p className={hintCls} data-testid="lms-section-passes">
        {passesUsed === null ? `At most ${passesMax} builder passes per draft.` : passesLine(passesUsed, passesMax)}
        {exhausted ? ' Edit the section by hand, or start a new build.' : ''}
      </p>

      <ConfirmModal
        isOpen={dialog === 'regenerate'}
        onClose={close}
        onConfirm={() => void submit()}
        title={`Regenerate section ${sectionNumber}?`}
        confirmLabel="Regenerate"
        loading={busy}
        message={
          <div className="space-y-3" data-testid="lms-section-regenerate-dialog">
            <p>
              The section&apos;s slides, reading and questions are rewritten from the same sources; your edits to this section are lost. Other sections are
              untouched.
            </p>
            {fileField}
            {errorBox}
          </div>
        }
      />

      <Modal isOpen={dialog === 'instruct'} onClose={close} title={`Apply an instruction to section ${sectionNumber}`} maxWidth="max-w-lg">
        <div className="space-y-3" data-testid="lms-section-instruct-dialog">
          <div className="space-y-1.5">
            <label className={labelCls} htmlFor={`lms-section-instruction-${sectionId}`}>
              What should the builder change in this section?
            </label>
            <textarea
              id={`lms-section-instruction-${sectionId}`}
              rows={4}
              maxLength={BUILDER_INSTRUCTION_MAX}
              value={instruction}
              onChange={(e) => setInstruction(e.target.value.slice(0, BUILDER_INSTRUCTION_MAX))}
              className={`${fieldCls} w-full`}
              data-testid="lms-section-instruction"
            />
            <p className={hintCls}>{`${instruction.length} of ${BUILDER_INSTRUCTION_MAX} characters. For example: ${EXAMPLES.map((e) => `"${e}"`).join(', ')}.`}</p>
          </div>
          <p className={hintCls}>The builder rewrites this section only, from the same sources; other sections are untouched.</p>
          {fileField}
          {errorBox}
          <div className="flex items-center justify-end gap-3 border-t border-gray-100 pt-3 dark:border-gray-700">
            <button type="button" className={`${secondaryBtnCls} disabled:cursor-not-allowed disabled:opacity-50`} onClick={close} disabled={busy}>
              Cancel
            </button>
            <button type="button" className={primaryBtnCls} onClick={() => void submit()} disabled={busy} data-testid="lms-section-instruct-submit">
              Apply the instruction
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default SectionActions;
